import { useMutation } from "@apollo/client";
import { Button, Modal } from "@navikt/ds-react";
import { type ReactElement, useEffect } from "react";
import {
  MarkSoknadReadDocument,
  MineSykmeldteDocument,
  type PreviewSoknadFragment,
} from "../../../../graphql/queries/graphql.generated";
import { formatDate } from "../../../../utils/dateUtils";
import { getSoknadActivationDate } from "../../../../utils/soknadUtils";

interface Props {
  isOpen: boolean;
  soknad: PreviewSoknadFragment;
  labelId: string;
  onOk: () => void;
}

const SoknadModalContent = ({
  isOpen,
  soknad,
  labelId,
  onOk,
}: Props): ReactElement => {
  switch (soknad.__typename) {
    case "PreviewFremtidigSoknad":
      return (
        <FremtidigSoknadModal id={labelId} tom={soknad.tom} onClick={onOk} />
      );
    case "PreviewNySoknad":
      return (
        <NySoknadModal
          id={labelId}
          soknadId={soknad.id}
          read={soknad.lest}
          onClick={onOk}
          isOpen={isOpen}
        />
      );
    case "PreviewSendtSoknad":
      throw new Error("Sendt should not use this modal content");
  }
};

function NySoknadModal({
  id,
  soknadId,
  read,
  onClick,
  isOpen,
}: {
  id: string;
  soknadId: string;
  read: boolean;
  onClick: () => void;
  isOpen: boolean;
}): ReactElement {
  const [markSoknadRead] = useMutation(MarkSoknadReadDocument);

  useEffect(() => {
    (async () => {
      if (!isOpen || read) return;
      await markSoknadRead({
        variables: { soknadId },
        refetchQueries: [{ query: MineSykmeldteDocument }],
      });
    })();
  }, [isOpen, markSoknadRead, soknadId, read]);

  return (
    <Modal.Body className="p-6">
      <p id={id} className="mr-12">
        Den ansatte har ikke sendt inn denne søknaden ennå.
      </p>
      <p>Du blir varslet så fort den er sendt.</p>
      <div className="flex justify-center">
        <Button
          className="mt-8 min-w-[8rem]"
          variant="secondary"
          size="small"
          onClick={onClick}
        >
          OK
        </Button>
      </div>
    </Modal.Body>
  );
}

function FremtidigSoknadModal({
  id,
  tom,
  onClick,
}: {
  id: string;
  tom: string;
  onClick: () => void;
}): ReactElement {
  return (
    <Modal.Body className="p-6">
      <h2 id={id}>Søknad er ikke klar</h2>
      <p>
        Den ansatte får ikke fylle ut søknaden før sykefraværet er over:{" "}
        <b>{formatDate(getSoknadActivationDate(tom))}</b>
      </p>
      <p>Du blir varslet så fort søknaden er utfylt og sendt inn.</p>
      <div className="flex justify-center">
        <Button
          className="mt-4 min-w-[8rem]"
          variant="secondary"
          size="small"
          onClick={onClick}
        >
          OK
        </Button>
      </div>
    </Modal.Body>
  );
}

export default SoknadModalContent;
